import { UserInfoFormValues } from "@/components/HolderForm/UserInfoFormValues.ts";
import { scrollToTop } from "@/helpers/scrollToTop.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { useReservationSubmit } from "@/pages/ReservationOverview/useReservationSubmit.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { UseFormHandleSubmit } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const useMobileDrawer = (
  handleSubmitHolderForm: UseFormHandleSubmit<UserInfoFormValues>,
  handleSubmitPaymentForm: () => Promise<void>,
  onSubmitComplete: () => void,
) => {
  const { t } = useTranslation();
  const { handleOpenCartDrawer, handleCloseCartDrawer } = useCartDrawerState();
  const { handleSubmitCart } = useReservationSubmit(onSubmitComplete);
  const { paymentEnabled } = useGetOrganization();

  const { page, setPage, setReservationHolderData, isForcedInquiry, acceptedGDPR, setIsGDPRHighlighted } =
    useEngineStore(
      useShallow((state) => ({
        page: state.page,
        setPage: state.setPage,
        setReservationHolderData: state.setReservationHolderData,
        isForcedInquiry: state.calculatedCartData?.isForcedInquiry,
        acceptedGDPR: state.acceptedGDPR,
        setIsGDPRHighlighted: state.setIsGDPRHighlighted,
      })),
    );

  const getMobileBtnText = () => {
    switch (page) {
      case AppPage.AccommodationPicking:
      case AppPage.ExtraOptions:
      case AppPage.HolderAndPaymentInfo:
        return t("continueShort");
      case AppPage.ReservationOverview:
        return isForcedInquiry ? t("sendInquiry") : t("finish");
      default:
        return t("continueShort");
    }
  };

  return {
    handleMobileContinue: async () => {
      switch (page) {
        case AppPage.AccommodationPicking:
          setPage(AppPage.ExtraOptions);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        case AppPage.ExtraOptions:
          setPage(AppPage.HolderAndPaymentInfo);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        case AppPage.HolderAndPaymentInfo:
          await handleSubmitHolderForm((data) => {
            setReservationHolderData(data);
          })();
          if (!isForcedInquiry && paymentEnabled) {
            await handleSubmitPaymentForm();
          }
          setPage(AppPage.ReservationOverview);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        case AppPage.ReservationOverview:
          if (acceptedGDPR) {
            void handleSubmitCart();
          } else {
            handleOpenCartDrawer();
            setIsGDPRHighlighted(true);
          }
          break;
        default:
          return;
      }
    },
    handleOpenCartDrawer,
    handleCloseCartDrawer,
    getMobileBtnText,
  };
};
